import { useState, useEffect, useMemo } from 'react';
import { getDealDetailsById } from '../../../../services/auctionSnapshot';
import {
  AMAZON,
  MAGNITE,
  ROKU,
  RESPONSE,
  CAS,
  VAST,
  DV360,
  MELI,
  TTD,
  AMAZON_DSP,
  DAS,
  MAGNITE_OPENRTB,
} from '../constants';
import { buildVastAdFields, createMultiRequestBidDetails } from './bidDetailsUtils';

export { getBidResponseMultiRequest, getVastRawDataMultiRequest } from './bidDetailsUtils';

const DEFAULT_BID_DETAILS_DATA = {
  bidWithVastData: null,
  bidResponse: null,
  adResponse: null,
  isAdResponseJson: false,
};

const getMagniteXMLByAdId = (xml, adId) => {
  try {
    const parser = new DOMParser();
    const serializer = new XMLSerializer();

    const doc = parser.parseFromString(xml, 'application/xml');
    const targetNode = doc.getElementById(adId);
    return serializer.serializeToString(targetNode);
  } catch (error) {
    console.error(`getMagniteXMLByAdId ${error}`);
    throw new Error('Incorrect Magnite XML!');
  }
};

const getBidDetailsFromXML = (xml) => {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xml, 'application/xml');
    const adId = doc?.getElementsByTagName('Ad')[0]?.getAttribute('id');
    const adSystem = doc?.getElementsByTagName('AdSystem')[0]?.textContent.replace(/(\n|^\s+)/gm, '');
    const adTitle = doc?.getElementsByTagName('AdTitle')[0]?.textContent.replace(/(\n|^\s+)/gm, '');
    return { adId, adSystem, adTitle };
  } catch (error) {
    console.error(`getBidDetailsFromXML ${error}`);
    throw new Error("Can't get bid details from XML!");
  }
};

const magniteBidDetails = ({ currentBid, integratorsData }) => {
  const magniteResponse = integratorsData?.[MAGNITE]?.[RESPONSE]?.record?.rawData;
  const bidWithVastData = {
    ...currentBid,
    ...getBidDetailsFromXML(getMagniteXMLByAdId(magniteResponse, currentBid?.demandBidId)),
  };
  const adResponse = getMagniteXMLByAdId(magniteResponse, bidWithVastData.adId);

  return { bidWithVastData, adResponse };
};

const rokuBidDetails = ({ currentBid, integratorsData, auctionSupplySource }) => {
  const { adm, ...rest } =
    integratorsData?.[ROKU]?.[RESPONSE]?.record?.rawData?.seatbid[0]?.bid?.find(
      (bid) => bid?.id === currentBid?.demandBidId
    ) || {};

  const vastRawData = integratorsData?.[ROKU]?.[VAST]?.record?.rawData;
  const isCasOrDas = auctionSupplySource === CAS || auctionSupplySource === DAS;

  return {
    bidWithVastData: isCasOrDas
      ? { ...currentBid, ...buildVastAdFields(vastRawData) }
      : { ...currentBid, ...getBidDetailsFromXML(vastRawData) },
    bidResponse: rest,
    adResponse: vastRawData,
    isAdResponseJson: isCasOrDas,
  };
};

const amazonBidDetails = ({ currentBid, integratorsData }) => {
  const vastRawData = integratorsData?.[AMAZON]?.[VAST]?.find(({ record }) => record?.rawData?.id === currentBid?.bidId)
    ?.record?.rawData;

  return {
    bidWithVastData: {
      ...currentBid,
      ...buildVastAdFields(vastRawData),
    },
    bidResponse: integratorsData?.[AMAZON]?.[RESPONSE]?.record?.rawData?.seatbid?.[0]?.bid?.find(
      (bid) => bid?.cridid === currentBid?.cridid
    ),
    adResponse: vastRawData?.ad,
    isAdResponseJson: true,
  };
};

const meliBidDetails = ({ currentBid, integratorsData }) => {
  const { adm, ...rest } =
    integratorsData?.[MELI]?.[RESPONSE]?.record?.rawData?.seatbid[0]?.bid?.find(
      (bid) => bid?.id === currentBid?.demandBidId
    ) || {};

  const vastRawData = integratorsData?.[MELI]?.[VAST]?.record?.rawData;

  return {
    bidWithVastData: {
      ...currentBid,
      ...buildVastAdFields(vastRawData),
    },
    bidResponse: rest,
    adResponse: vastRawData,
    isAdResponseJson: true,
  };
};

const bidDetailsMap = {
  [MAGNITE]: magniteBidDetails,
  [ROKU]: rokuBidDetails,
  [AMAZON]: amazonBidDetails,
  [DV360]: createMultiRequestBidDetails(DV360),
  [MELI]: meliBidDetails,
  [TTD]: createMultiRequestBidDetails(TTD),
  [AMAZON_DSP]: createMultiRequestBidDetails(AMAZON_DSP, { includeCrid: false }),
  [MAGNITE_OPENRTB]: createMultiRequestBidDetails(MAGNITE_OPENRTB),
};

const resolveBidDetails = (bidIntegrator, data) => {
  const fn = bidDetailsMap[bidIntegrator];
  return fn ? fn(data) : DEFAULT_BID_DETAILS_DATA;
};

export const useBidDetails = (integratorsData, auctionSupplySource) => {
  const [isOpenBidDetails, setIsOpenBidDetails] = useState(false);
  const [currentBid, setCurrentBid] = useState(null);
  const [dealDetails, setDealDetails] = useState(null);
  const [isDealDetailsLoading, setIsDealDetailsLoading] = useState(false);
  const [dealDetailsError, setIsDealDetailsError] = useState(null);

  const bidDetailsData = useMemo(() => {
    if (!currentBid) {
      return DEFAULT_BID_DETAILS_DATA;
    }

    const bidIntegrator = currentBid.demandPartner?.toUpperCase();

    return {
      ...DEFAULT_BID_DETAILS_DATA,
      ...resolveBidDetails(bidIntegrator, { currentBid, integratorsData, auctionSupplySource }),
      dealDetails,
    };
  }, [auctionSupplySource, currentBid, dealDetails, integratorsData]);

  const openBidDetails = (bid) => {
    setIsOpenBidDetails(true);
    setCurrentBid(bid);
  };

  const closeBidDetails = () => {
    setIsOpenBidDetails(false);
    setCurrentBid(null);
    setDealDetails(null);
    setIsDealDetailsError(null);
  };

  useEffect(() => {
    const dealId = currentBid?.dealId;

    if (!dealId) {
      setDealDetails(null);
      setIsDealDetailsLoading(false);
      return;
    }

    let isCancelled = false;
    setIsDealDetailsLoading(true);
    setIsDealDetailsError(null);

    getDealDetailsById(dealId)
      .then((details) => {
        if (!isCancelled) {
          setDealDetails(details);
        }
      })
      .catch((error) => {
        if (!isCancelled) {
          setIsDealDetailsError(error);
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsDealDetailsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [currentBid?.dealId]);

  return {
    isOpenBidDetails,
    bidDetailsData,
    isBidDetailsLoading: isDealDetailsLoading,
    dealDetailsError,
    openBidDetails,
    closeBidDetails,
  };
};
